import clsx from "clsx";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
    type ReactNode,
    type SVGProps,
} from "react";
import { createPortal } from "react-dom";

import {
    ToastContext,
    type ToastContextValue,
    type ToastOptions,
    type ToastVariant,
} from "./toast-context";

type ToastItem = {
    id: string;
    title: string;
    description?: string;
    variant: ToastVariant;
    action?: ToastOptions["action"];
    /** True from the moment it is dismissed until the exit animation has run. */
    leaving: boolean;
};

const DEFAULT_DURATION_MS = 4000;

/** The most toasts on screen at once; a fourth sends the oldest out. */
export const MAX_VISIBLE_TOASTS = 3;

/**
 * How long a dismissed toast stays mounted so its exit can be seen. Mirrors
 * `--animate-fade-out` in `motion.css`; the two have to agree, or the node is
 * either pulled out mid-fade or left sitting invisible after it.
 */
const EXIT_MS = 250;

/**
 * A module counter rather than `crypto.randomUUID()`: ids only need to be unique
 * within this list, and a pure incrementing value keeps the call free of the
 * impurity the react-hooks rules flag — and free of the SSR mismatch a random
 * one would introduce in the prerendered apps.
 */
let nextToastId = 0;

/**
 * "Has this rendered on the client yet", as a store rather than a mount flag in
 * state: `useSyncExternalStore` is given a server snapshot and a client one, so
 * React resolves the difference during hydration instead of the provider
 * setting state from an effect and re-rendering the whole tree under it.
 * Nothing ever changes, so the subscription is a no-op.
 */
const subscribeToNothing = () => () => {};
const onTheClient = () => true;
const onTheServer = () => false;

function icon(path: ReactNode) {
    return function Icon(props: SVGProps<SVGSVGElement>) {
        return (
            <svg
                width={14}
                height={14}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                {...props}
            >
                {path}
            </svg>
        );
    };
}

const IconCheck = icon(<path d="M8 12.5l2.6 2.6L16 9.5" />);
const IconX = icon(
    <>
        <path d="M18 6L6 18" />
        <path d="M6 6l12 12" />
    </>,
);
const IconAlert = icon(
    <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7.5v5M12 16.5h.01" />
    </>,
);
const IconInfo = icon(
    <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 16.5v-5M12 7.5h.01" />
    </>,
);

const VARIANTS: Record<ToastVariant, { icon: typeof IconCheck; toneClassName: string }> = {
    success: { icon: IconCheck, toneClassName: "text-toast-success" },
    info: { icon: IconInfo, toneClassName: "text-toast-info" },
    error: { icon: IconAlert, toneClassName: "text-toast-danger" },
};

/**
 * The application's toast layer, and the only one: the shared packages'
 * components read this same context through `useToast`, so an app that mounts
 * a provider of its own gets a second, competing one and silently drops their
 * notifications on the floor.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    /**
     * Per toast: `hide` is the auto-dismiss, `remove` the unmount that follows
     * the exit animation. `remove` being set is also what marks a toast as
     * already on its way out, so a second dismiss can be ignored rather than
     * pushing the removal further out each time the button is pressed — a real
     * 150ms window on a control that stays under the pointer.
     */
    const timers = useRef(new Map<string, { hide?: number; remove?: number }>());

    /** The toasts not yet dismissed, oldest first, for the cap. */
    const showing = useRef<string[]>([]);

    const dismiss = useCallback((id: string) => {
        const timer = timers.current.get(id);

        if (!timer || timer.remove !== undefined) {
            return;
        }

        showing.current = showing.current.filter((held) => held !== id);

        if (timer.hide !== undefined) {
            window.clearTimeout(timer.hide);
            timer.hide = undefined;
        }

        setToasts((current) =>
            current.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast)),
        );

        timer.remove = window.setTimeout(() => {
            timers.current.delete(id);
            setToasts((current) => current.filter((toast) => toast.id !== id));
        }, EXIT_MS);
    }, []);

    const show = useCallback(
        ({
            variant,
            title,
            description,
            action,
            duration = DEFAULT_DURATION_MS,
        }: ToastOptions & { variant: ToastVariant }) => {
            const id = String(++nextToastId);

            setToasts((current) => [
                ...current,
                { id, title, description, action, variant, leaving: false },
            ]);

            timers.current.set(id, {
                hide: window.setTimeout(() => dismiss(id), duration),
            });

            showing.current = [...showing.current, id];
            while (showing.current.length > MAX_VISIBLE_TOASTS) {
                dismiss(showing.current[0] as string);
            }
        },
        [dismiss],
    );

    // Clear pending timers if the provider itself unmounts.
    useEffect(() => {
        const pending = timers.current;

        return () => {
            for (const timer of pending.values()) {
                if (timer.hide !== undefined) window.clearTimeout(timer.hide);
                if (timer.remove !== undefined) window.clearTimeout(timer.remove);
            }

            pending.clear();
            showing.current = [];
        };
    }, []);

    /**
     * Portalled for the same reason a modal is: `fixed` is relative to the
     * nearest transformed ancestor, and webapp's sidebar has one — a toast
     * raised from in there would be trapped inside the 224px rail. Gated on a
     * mounted flag so the prerendered apps do not reach for `document` on the
     * server; there are no toasts on the first paint, so nothing is missing
     * from the markup this skips.
     */
    const mounted = useSyncExternalStore(subscribeToNothing, onTheClient, onTheServer);

    const value = useMemo<ToastContextValue>(
        () => ({
            show,
            success: (title, description) => show({ variant: "success", title, description }),
            info: (title, description) => show({ variant: "info", title, description }),
            error: (title, description) => show({ variant: "error", title, description }),
            dismiss,
        }),
        [show, dismiss],
    );

    return (
        <ToastContext.Provider value={value}>
            {children}

            {mounted
                ? createPortal(
                      <div className="pointer-events-none fixed bottom-24 right-4 md:bottom-4 z-[60] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2">
                          {toasts.map((toast) => {
                              const { icon: Icon, toneClassName } = VARIANTS[toast.variant];

                              return (
                                  <div
                                      key={toast.id}
                                      role="status"
                                      aria-live="polite"
                                      className={clsx(
                                          "flex items-start gap-2.5 rounded-[10px] border border-toast-border bg-toast-surface px-3.5 py-2.5 shadow-lg shadow-black/30",
                                          toast.leaving
                                              ? "pointer-events-none motion-safe:animate-fade-out"
                                              : "pointer-events-auto motion-safe:animate-fade-in",
                                      )}
                                  >
                                      <span className={clsx("mt-px shrink-0", toneClassName)}>
                                          <Icon />
                                      </span>

                                      <div className="min-w-0 flex-1">
                                          <p className="text-[12.5px] text-toast-fg">
                                              {toast.title}
                                          </p>

                                          {toast.description ? (
                                              <p className="mt-1 text-[11.5px] text-toast-fg-muted">
                                                  {toast.description}
                                              </p>
                                          ) : null}
                                          {toast.action && (
                                              <button
                                                  type="button"
                                                  data-analytics-action="notification_action_clicked"
                                                  onClick={() => {
                                                      toast.action?.onClick();
                                                      dismiss(toast.id);
                                                  }}
                                                  className="mt-2 rounded text-[12px] font-semibold text-toast-success underline-offset-4 hover:underline focus-visible:outline-2"
                                              >
                                                  {toast.action.label}
                                              </button>
                                          )}
                                      </div>

                                      <button
                                          type="button"
                                          onClick={() => dismiss(toast.id)}
                                          aria-label="Dismiss notification"
                                          data-analytics-action="notification_dismissed"
                                          data-analytics-context={JSON.stringify({
                                              source: "Toast",
                                              option: toast.variant,
                                          })}
                                          className="-m-3.5 grid size-11 shrink-0 cursor-pointer place-items-center rounded text-toast-fg-faint transition-colors hover:text-toast-fg"
                                      >
                                          <IconX width={13} height={13} />
                                      </button>
                                  </div>
                              );
                          })}
                      </div>,
                      document.body,
                  )
                : null}
        </ToastContext.Provider>
    );
}
